'use client';

import { ReactFlowProvider, Node, useReactFlow } from '@xyflow/react';
import { DemoCanvas } from '@/components/demo/demo-canvas';
import { demoCanvasConfig } from '@/components/demo/demo-canvas-config';
import { CanvasProvider } from '@/providers/canvas';
import { ProjectProvider } from '@/providers/project';
import { useEffect, useMemo, useState, useCallback } from 'react';

const mockProject = {
  id: '',
  name: 'Embed Project',
  transcriptionModel: '',
  visionModel: '',
  createdAt: new Date(),
  updatedAt: null as Date | null,
  content: demoCanvasConfig,
  userId: 'embed-user',
  image: null as string | null,
  members: [] as string[] | null,
  welcomeProject: false,
};

/**
 * 内部组件：使用 ReactFlow hook 来适配视图，自动居中并缩放所有节点
 */
function AutoFitView({ nodes, workflowKey }: { nodes: Node[]; workflowKey: number }) {
  const { fitView } = useReactFlow();

  // 居中并适配视图的函数
  const centerAndFit = useCallback(() => {
    if (!nodes || nodes.length === 0) return;

    try {
      // 使用 fitView 自动适配所有节点，确保完整显示
      // fitView 会自动计算所有节点的边界框，然后居中并缩放到合适大小
      fitView({
        padding: 0.25,  // 增加内边距，让节点之间留出更多空间
        maxZoom: 0.7,   // 降低最大缩放，节点显示更小
        minZoom: 0.1,   // 降低最小缩放，确保高的工作流也能完整显示
        duration: 300,  // 动画时长 300ms
      });
    } catch (e) {
      // 忽略错误，可能在组件未完全初始化时调用
      console.debug('fitView error:', e);
    }
  }, [nodes, fitView]);

  useEffect(() => {
    // 延迟执行以确保节点已渲染
    const timer = setTimeout(() => {
      centerAndFit();
    }, 150); // 增加延迟以确保 DOM 完全渲染

    // 监听窗口大小变化，重新居中
    let resizeTimer: NodeJS.Timeout | null = null;
    const handleResize = () => {
      // 清除之前的定时器，使用防抖避免频繁调用
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        centerAndFit();
      }, 300);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      if (resizeTimer) clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [centerAndFit, workflowKey]); // 当节点变化或工作流切换时重新居中

  return null;
}

export default function EmbedCanvasPage() {
  // 深色由 providers/theme.tsx 路由级 forcedTheme 处理（/demo 固定 dark）。
  const workflows = useMemo(() => [
    demoCanvasConfig,
    demoCanvasConfig,
    demoCanvasConfig,
  ], []);
  const backgrounds = ['#000000', '#000000', '#000000'];
  const [active, setActive] = useState(0);

  return (
    <div
      className="relative w-full h-screen overflow-hidden bg-background"
      style={{ ['--canvas-bg' as any]: backgrounds[active] }}
    >
      <ReactFlowProvider>
        <ProjectProvider data={mockProject as any}>
          <CanvasProvider>
            <DemoCanvas
              defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
              autoPanOnNodeDrag={false}
              minZoom={0.1}
              maxZoom={0.7}
              zoomOnScroll={false}
              panOnScroll={false}
              preventScrolling={false}
              nodes={workflows[active].nodes as any}
              edges={workflows[active].edges as any}
              forceDark
            >
              <AutoFitView nodes={workflows[active].nodes as any} workflowKey={active} key={active} />
            </DemoCanvas>
          </CanvasProvider>
        </ProjectProvider>
      </ReactFlowProvider>

      {/* Workflow pager dots */}
      <div className="pointer-events-none absolute inset-x-0 bottom-4 flex items-center justify-center gap-2">
        {[0,1,2].map((i) => (
          <button
            key={i}
            aria-label={`Switch to workflow ${i+1}`}
            onClick={() => setActive(i)}
            className={`${active===i ? 'bg-white/90' : 'bg-white/40 hover:bg-white/70'} pointer-events-auto h-2.5 w-2.5 rounded-full transition`}
          />
        ))}
      </div>
    </div>
  );
}


